import { motion } from 'framer-motion'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

const bullets = [
    'Possa não se sentir confortável em abordar as questões relacionadas com a sua identidade de género e/ou orientação sexual com as autoridades e os serviços de apoio;',
    'Enfrente desafios acrescidos no contacto com profissionais do sistema de justiça e autoridades policiais;',
    'Receie denunciar essas situações por medo de sofrer represálias e/ou ver a sua vida íntima exposta a pessoas próximas, colegas e/ou familiares;',
    'Hesite em denunciar situações que porventura possam reforçar os estereótipos ligados às pessoas LGBTI+;',
    'Tenha dúvidas sobre se o que lhe aconteceu é efetivamente crime e possa sentir alguma culpa associada a esta dúvida – Lembre-se que nada do que possa ter acontecido é culpa sua, independentemente das circunstâncias em que aconteceu.'
]

const numberColors = [
    'from-brand-500 to-pink-500',
    'from-brand-500 to-pink-500',
    'from-brand-500 to-pink-500',
    'from-brand-500 to-pink-500',
    'from-brand-500 to-pink-500',
]

export default function Home() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen"
        >
            {/* h1 para leitores de ecrã e SEO: o mote do site está rasterizado na imagem do hero */}
            <h1 className="sr-only">Grite pelos seus direitos — apoio a vítimas LGBTI+ da APAV</h1>
            <title>Apoio a Vítimas LGBTI+ de Crime e Violência | APAV</title>
            <meta name="description" content="Informação da APAV para vítimas LGBTI+ de crime ou violência: os obstáculos à denúncia, a vulnerabilidade acrescida e o que significa a sigla LGBTI+." />
            <link rel="canonical" href="https://lgbti.apav.pt/" />
            {/* Hero Banner */}
            <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-full pt-20 lg:pt-28 bg-white"
            >
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <img
                        src="/assets/images/wallpaper-vdpms-3.png"
                        alt="Grite pelos seus direitos — APAV LGBTI+"
                        className="w-full h-auto rounded-2xl shadow-xl shadow-slate-900/10"
                    />
                </div>
            </motion.section>

            {/* Content Section 1 — Numbered Cards */}
            <section className="pt-16 lg:pt-20 pb-16 lg:pb-24 bg-white">
                <div className="container-custom">
                    <div className="max-w-4xl mx-auto">
                        <motion.h2
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl lg:text-3xl font-bold text-slate-900 mb-10 leading-tight"
                        >
                            Compreendemos que, se é LGBTI+ e foi vítima de crime ou violência:
                        </motion.h2>
                        <div className="space-y-4">
                            {bullets.map((text, i) => (
                                <motion.div
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.07 }}
                                    className="flex gap-5 items-start bg-white border border-slate-100 rounded-2xl p-6 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${numberColors[i]} flex items-center justify-center text-white font-bold text-base shadow-md`}>
                                        {i + 1}
                                    </div>
                                    <p className="text-slate-700 leading-relaxed text-lg pt-1">{text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Section 2 — Pull Quote */}
            <section className="section-padding bg-gradient-to-br from-slate-900 via-brand-900 to-purple-900 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-brand-400 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2" />
                </div>
                <div className="container-custom relative z-10">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="max-w-4xl mx-auto"
                    >
                        <span className="text-6xl text-brand-400/40 font-serif leading-none select-none" aria-hidden="true">"</span>
                        <p className="text-xl lg:text-2xl font-semibold text-white leading-relaxed -mt-4">
                            A vulnerabilidade acrescida das pessoas LGBTI+ advém da estigmatização das suas identidades e características. Estas pessoas são, frequentemente, alvo de sentimentos e atitudes negativas que enfraquecem a fruição dos seus direitos. São também, por vezes, julgadas com base em preconceitos e estereótipos que desconsideram a sua individualidade. Simultaneamente, a interseccionalidade com outras características que habitualmente são afetadas por sistemas de desigualdade reforça as múltiplas formas de discriminação a que as pessoas LGBTI+ são sujeitas nas várias dimensões da sua vida.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Definition Section */}
            <section className="section-padding bg-gradient-to-br from-brand-50 to-purple-50">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <div className="border-t-4 border-brand-600 pt-8 pb-8 border-b-4">
                            <p className="text-2xl lg:text-3xl font-medium italic text-slate-800 leading-relaxed">
                                A sigla LGBTI+ significa pessoas Lésbicas, Gays, Bissexuais, Trans e Intersexo, sendo que o sinal (+) indica a inclusão de todas as identidades que não estejam nesta sigla representadas por letras.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
